import { useEffect, useRef, useState } from 'react';

import { useEvent } from 'hooks/reactuse';

/** The use web socket url type */
export type UseWebSocketUrl = string | (() => string);

/** The use web socket options type */
export interface UseWebSocketOptions {
  /** The list of protocols to use */
  protocols?: Array<'soap' | 'wasm'>;
  /** The number of times to retry the connection */
  retry?: boolean | number;
  /** The callback function that is called when the WebSocket connection is established */
  onConnected?: (webSocket: WebSocket) => void;
  /** The callback function that is called when the WebSocket connection is closed */
  onDisconnected?: (event: CloseEvent, webSocket: WebSocket) => void;
  /** The callback function that is called when an error occurs */
  onError?: (event: Event, webSocket: WebSocket) => void;
  /** The callback function that is called when a message is received */
  onMessage?: (event: MessageEvent, webSocket: WebSocket) => void;
  /** Готов ли клиент подключаться к сокетам, когда готов - автоматическое подключение */
  ready?: boolean;
  /** The callback function that is called when the Websocket connection is restored */
  onReconnect?: (webSocket: WebSocket) => void;
}

export type UseWebSocketStatus =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'disconnected'
  | 'error';

/** The use web socket return type */
export interface UseWebSocketReturn {
  /** The WebSocket client */
  client?: WebSocket;
  /** The close function */
  close: WebSocket['close'];
  /** The send function */
  send: WebSocket['send'];
  sendObj: (obj: AnyObject) => void;
  /** The status of the WebSocket connection */
  status: UseWebSocketStatus;
  /** The open function */
  open: () => void;
}

/**
 * @name useWebSocket
 * @description - Hook that connects to a WebSocket server and handles incoming and outgoing messages
 * @category Network
 *
 * @param {UseWebSocketUrl} url The URL of the WebSocket server
 * @param {(webSocket: WebSocket) => void} [options.onConnected] The callback function that is called when the WebSocket connection is established
 * @param {(event: CloseEvent, webSocket: WebSocket) => void} [options.onDisconnected] The callback function that is called when the WebSocket connection is closed
 * @param {(event: Event, webSocket: WebSocket) => void} [options.onError] The callback function that is called when an error occurs
 * @param {(event: MessageEvent, webSocket: WebSocket) => void} [options.onMessage] The callback function that is called when a message is received
 * @param {boolean | number} [options.retry] The number of times to retry the connection
 * @param {Array<'soap' | 'wasm'>} [options.protocols] The list of protocols to use
 * @returns {UseWebSocketReturn} An object with the status, close, send, open, and ws properties
 *
 * @example
 * const { status, close, send, open, client } = useWebSocket('url');
 */
export const useWebSocket = (url: UseWebSocketUrl, options?: UseWebSocketOptions): UseWebSocketReturn => {
  const webSocketRef = useRef<WebSocket>();
  const reconnectAttemptRef = useRef(0);
  const explicitlyClosedRef = useRef(false);
  const isMountedRef = useRef(true);
  const wasReconnectingRef = useRef(false);

  const queueRef = useRef<(string | Blob | ArrayBufferLike | ArrayBufferView)[]>([]);
  const reconnectTimeoutRef = useRef<number>();
  const pingIntervalRef = useRef<number>();

  const [status, setStatus] = useState<UseWebSocketStatus>('idle');

  const getUrl = () => (typeof url === 'function' ? url() : url);

  const flushQueue = () => {
    if (!webSocketRef.current || webSocketRef.current.readyState !== WebSocket.OPEN) return;

    queueRef.current.forEach((msg) => webSocketRef.current!.send(msg));
    queueRef.current = [];
  };

  const getBackoffDelay = (attempt: number) => {
    const base = 500; // 0.5s
    const max = 10_000; // 10s
    const jitter = Math.random() * 300;
    return Math.min(base * 2 ** attempt + jitter, max);
  };

  const send = (data: string | Blob | ArrayBufferLike | ArrayBufferView) => {
    if (webSocketRef.current?.readyState === WebSocket.OPEN) {
      webSocketRef.current.send(data);
    } else {
      queueRef.current.push(data);
    }
  };

  const sendObj = (obj: AnyObject) => {
    const message = JSON.stringify(obj);
    send(message);
  };

  const close = () => {
    explicitlyClosedRef.current = true;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    webSocketRef.current?.close();
  };

  const connect = useEvent(() => {
    if (webSocketRef.current && webSocketRef.current.readyState !== WebSocket.CLOSED) {
      return;
    }

    const ws = new WebSocket(getUrl(), options?.protocols);
    webSocketRef.current = ws;

    setStatus(reconnectAttemptRef.current > 0 ? 'reconnecting' : 'connecting');

    ws.onopen = () => {
      const isReconnect = wasReconnectingRef.current;

      reconnectAttemptRef.current = 0;
      setStatus('connected');

      flushQueue();

      if (isReconnect) {
        options?.onReconnect?.(ws);
        wasReconnectingRef.current = false;
      } else {
        options?.onConnected?.(ws);
      }

      pingIntervalRef.current = window.setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ event: 'ping' }));
        }
      }, 10_000);
    };

    ws.onerror = (event) => {
      setStatus('error');
      options?.onError?.(event, ws);
    };

    ws.onmessage = (event) => {
      options?.onMessage?.(event, ws);
    };

    ws.onclose = (event) => {
      setStatus('disconnected');
      options?.onDisconnected?.(event, ws);

      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }

      if (!isMountedRef.current) return;
      if (explicitlyClosedRef.current) return;

      // целенаправленное закрытие -> не реконнектимся
      if (event.code === 1000) return;

      const maxRetries = options?.retry ? getRetry(options.retry) : 0;

      if (reconnectAttemptRef.current < maxRetries) {
        wasReconnectingRef.current = true;

        const delay = getBackoffDelay(reconnectAttemptRef.current);
        reconnectAttemptRef.current += 1;

        reconnectTimeoutRef.current = window.setTimeout(() => {
          connect();
        }, delay);
      }
    };
  });

  useEffect(() => {
    isMountedRef.current = true;

    if (options?.ready === false) {
      return;
    }

    connect();

    return () => {
      isMountedRef.current = false;

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }

      webSocketRef.current?.close();
      webSocketRef.current = undefined;
    };
  }, [url, options?.ready]);

  const open = () => {
    explicitlyClosedRef.current = false;
    connect();
  };

  return { client: webSocketRef.current, close, open, send, sendObj, status };
};

const getRetry = (retry: number | boolean) => {
  if (typeof retry === 'number') return retry;
  return retry ? 1 : 0;
};
